import { useState, useCallback, useRef } from 'react';
import predictiveService from 'portal/services/predictiveService';
import { mockEnabled } from 'portal/mockData/mockConfig';
import {
  mockRevenueForecast,
  mockRevenueForecastExtended,
  mockDropoutRisk,
  mockTherapistPerformance,
  mockBusinessAdvisor,
  mockDataHealth,
} from 'portal/mockData/mockPredictive';

// ─────────────────────────────────────────────────────────────────────────────
// Simple in-memory cache to avoid redundant API calls within the same session
// ─────────────────────────────────────────────────────────────────────────────
const cache = {};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function getCached(key) {
  const entry = cache[key];
  if (entry && Date.now() - entry.timestamp < CACHE_TTL) return entry.data;
  return null;
}

function setCache(key, data) {
  cache[key] = { data, timestamp: Date.now() };
}

// Wipe every entry — called on login/logout/impersonation (see store.js) so a
// prior session's (demo or otherwise) predictive data can never leak into the
// next one via this module-scope cache, which outlives React/Redux state.
export function clearPredictiveCache() {
  Object.keys(cache).forEach((key) => delete cache[key]);
}

// ─────────────────────────────────────────────────────────────────────────────
// Generic fetcher — wraps any predictive service method
// ─────────────────────────────────────────────────────────────────────────────
function usePredictiveFetcher(cacheKey) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  const execute = useCallback(
    async (serviceFn, ...args) => {
      // Return cached data if available
      const cached = getCached(cacheKey);
      if (cached) {
        setData(cached);
        return cached;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await serviceFn(...args);
        console.log(`[Predictive:${cacheKey}] Response:`, response);

        if (response?.rescode === 1) {
          setData(response.data);
          setCache(cacheKey, response.data);
          return response.data;
        } else {
          const msg = response?.message || 'Failed to fetch predictive data';
          console.warn(`[Predictive:${cacheKey}] API error:`, msg);
          setError(msg);
        }
        return null;
      } catch (err) {
        console.error(`[Predictive:${cacheKey}] Exception:`, err);
        setError(err?.message || 'Something went wrong');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [cacheKey]
  );

  const reset = useCallback(() => {
    fetchedRef.current = false;
    delete cache[cacheKey];
  }, [cacheKey]);

  return { data, loading, error, execute, fetchedRef, reset };
}

// ─────────────────────────────────────────────────────────────────────────────
// useDropoutRisk — Patient dropout risk predictions
// ─────────────────────────────────────────────────────────────────────────────
export function useDropoutRisk() {
  const { data, loading, error, execute, fetchedRef } = usePredictiveFetcher('dropout-risk');

  const fetch = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    if (mockEnabled()) return execute(() => Promise.resolve({ rescode: 1, data: mockDropoutRisk }));
    return execute(predictiveService.getDropoutRisk);
  }, [execute, fetchedRef]);

  return { data, loading, error, fetch };
}

// ─────────────────────────────────────────────────────────────────────────────
// useRevenueForecast — Revenue prediction for next month
// ─────────────────────────────────────────────────────────────────────────────
export function useRevenueForecast() {
  const { data, loading, error, execute, fetchedRef } = usePredictiveFetcher('revenue-forecast');

  const fetch = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    if (mockEnabled()) return execute(() => Promise.resolve({ rescode: 1, data: mockRevenueForecast }));
    return execute(predictiveService.getRevenueForecast);
  }, [execute, fetchedRef]);

  return { data, loading, error, fetch };
}

// ─────────────────────────────────────────────────────────────────────────────
// useRevenueForecastExtended — Multi-month revenue forecast
// ─────────────────────────────────────────────────────────────────────────────
export function useRevenueForecastExtended() {
  const { data, loading, error, execute, fetchedRef } = usePredictiveFetcher('revenue-forecast-extended');

  const fetch = useCallback(async (months = 6) => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    if (mockEnabled()) return execute(() => Promise.resolve({ rescode: 1, data: mockRevenueForecastExtended }));
    return execute(predictiveService.getRevenueForecastExtended, months);
  }, [execute, fetchedRef]);

  return { data, loading, error, fetch };
}

// ─────────────────────────────────────────────────────────────────────────────
// useTherapistPerformance — Therapist performance rankings
// ─────────────────────────────────────────────────────────────────────────────
export function useTherapistPerformance() {
  const { data, loading, error, execute, fetchedRef } = usePredictiveFetcher('therapist-performance');

  const fetch = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    if (mockEnabled()) return execute(() => Promise.resolve({ rescode: 1, data: mockTherapistPerformance }));
    return execute(predictiveService.getTherapistPerformance);
  }, [execute, fetchedRef]);

  return { data, loading, error, fetch };
}

// ─────────────────────────────────────────────────────────────────────────────
// useBusinessAdvisor — Smart business recommendations
// ─────────────────────────────────────────────────────────────────────────────
export function useBusinessAdvisor() {
  const { data, loading, error, execute, fetchedRef } = usePredictiveFetcher('business-advisor');

  const fetch = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    if (mockEnabled()) return execute(() => Promise.resolve({ rescode: 1, data: mockBusinessAdvisor }));
    return execute(predictiveService.getBusinessAdvisor);
  }, [execute, fetchedRef]);

  return { data, loading, error, fetch };
}

// ─────────────────────────────────────────────────────────────────────────────
// useDataHealth — Booking/billing integrity anomalies (duplicate bookings,
// zero-duration slots, invoice ledger drift)
// ─────────────────────────────────────────────────────────────────────────────
export function useDataHealth() {
  const { data, loading, error, execute, fetchedRef } = usePredictiveFetcher('data-health');

  const fetch = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    if (mockEnabled()) return execute(() => Promise.resolve({ rescode: 1, data: mockDataHealth }));
    return execute(predictiveService.getDataHealth);
  }, [execute, fetchedRef]);

  return { data, loading, error, fetch };
}
